/** Commandes physiques ; le client ne fournit jamais les écritures du journal. */
import { z } from "zod";

import { idSchema } from "./parcellaire.js";
import { dateIsoSchema } from "./vendange.js";

/** NUMERIC(16,3), sans passage par un flottant ni arrondi silencieux. */
export const volumeLiquideSchema = z
  .string()
  .regex(/^(?:0|[1-9]\d{0,12})(?:\.\d{1,3})?$/, "Saisissez des litres, avec au plus 3 décimales.");
export const volumeLiquidePositifSchema = volumeLiquideSchema.refine(
  (volume) => /[1-9]/.test(volume),
  "Le volume doit être supérieur à zéro.",
);
export const versionStockLiquideSchema = z.string().regex(/^(?:0|[1-9]\d{0,17})$/);
export const homogeneiteLiquideSchema = z.enum(["confirmee", "non_confirmee", "non_homogene"]);
export type HomogeneiteLiquide = z.infer<typeof homogeneiteLiquideSchema>;

export const natureLotLiquideSchema = z.enum([
  "mout_brut",
  "mout_net",
  "vin",
  "preparation",
  "sous_produit",
]);
export const typeSousProduitLiquideSchema = z.enum([
  "bourbes",
  "rebeche",
  "lies_cuverie",
  "bvd",
  "melange_sous_produits",
  "a_qualifier",
]);

export const contexteCommandeLiquideSchema = z.object({
  uuid_commande: z.string().uuid(),
  schema_version: z.literal(1),
  version_bascule: z.string().min(1).max(80),
  date_operation: dateIsoSchema,
  heure_operation: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .optional(),
  operateur_membre_id: idSchema.optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type ContexteCommandeLiquide = z.infer<typeof contexteCommandeLiquideSchema>;

/** Version physique exigée pour qualifier tout ou partie d'une position en réserve. */
export const contexteAffectationReserveSchema = contexteCommandeLiquideSchema
  .pick({ uuid_commande: true, schema_version: true, version_bascule: true })
  .extend({
    position_id: idSchema,
    version_position_attendue: versionStockLiquideSchema,
  });
export type ContexteAffectationReserve = z.infer<typeof contexteAffectationReserveSchema>;

export const prelevementPositionLiquideSchema = z.object({
  position_id: idSchema,
  version_attendue: versionStockLiquideSchema,
  volume_l: volumeLiquidePositifSchema,
});

/** Une route = des prélèvements effectivement réunis dans UN contenant. */
export const trajetLiquideSchema = z
  .object({
    sources: z
      .array(
        prelevementPositionLiquideSchema.extend({
          // Si plusieurs sources ont subi des pertes avant mélange, mesurer la
          // quantité réellement reçue de chacune : ne pas inventer sa répartition.
          volume_recu_l: volumeLiquideSchema.optional(),
          reserve_prelevee: z
            .array(
              z.object({
                reserve_id: idSchema,
                volume_l: volumeLiquidePositifSchema,
              }),
            )
            .max(50)
            .optional(),
        }),
      )
      .min(1)
      .max(50),
    contenant_destination_id: idSchema,
    version_destination_attendue: versionStockLiquideSchema,
    volume_recu_l: volumeLiquidePositifSchema,
    perte_l: volumeLiquideSchema.default("0"),
    motif_perte: z.string().trim().min(3).max(500).optional(),
    homogeneite_resultat: homogeneiteLiquideSchema,
  })
  .superRefine((trajet, ctx) => {
    if (
      new Set(trajet.sources.map((source) => source.position_id)).size !== trajet.sources.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Une position ne peut être prélevée deux fois dans le même trajet.",
        path: ["sources"],
      });
    }
    if (/[1-9]/.test(trajet.perte_l) && !trajet.motif_perte) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Précisez la perte réellement constatée.",
        path: ["motif_perte"],
      });
    }
  });

export const deplacementLiquideInputSchema = contexteCommandeLiquideSchema
  .extend({
    type_operation: z.enum(["transfert", "melange"]),
    trajets: z.array(trajetLiquideSchema).min(1).max(20),
  })
  .superRefine((commande, ctx) => {
    const destinations = commande.trajets.map((trajet) => trajet.contenant_destination_id);
    if (new Set(destinations).size !== destinations.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Réunissez dans un même trajet les sources vers un même contenant.",
        path: ["trajets"],
      });
    }
  });
export type DeplacementLiquideInput = z.infer<typeof deplacementLiquideInputSchema>;

/** Soutirage mesuré : le vin et les lies ont deux destinations physiques distinctes. */
export const soutirageLiquideInputSchema = contexteCommandeLiquideSchema
  .extend({
    source: prelevementPositionLiquideSchema,
    contenant_destination_id: idSchema,
    version_destination_attendue: versionStockLiquideSchema,
    volume_vin_recu_l: volumeLiquidePositifSchema,
    homogeneite_vin_resultat: homogeneiteLiquideSchema,
    contenant_lies_id: idSchema,
    version_contenant_lies_attendue: versionStockLiquideSchema,
    volume_lies_l: volumeLiquidePositifSchema,
    perte_l: volumeLiquideSchema.default("0"),
    motif_perte: z.string().trim().min(3).max(500).optional(),
    methode_mesure: z.string().trim().min(2).max(80).optional(),
  })
  .superRefine((commande, ctx) => {
    const contenants = [commande.contenant_destination_id, commande.contenant_lies_id];
    if (new Set(contenants).size !== contenants.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Le vin soutiré et les lies doivent avoir deux destinations distinctes.",
        path: ["contenant_lies_id"],
      });
    }
    if (/[1-9]/.test(commande.perte_l) && !commande.motif_perte) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Précisez la perte réellement constatée.",
        path: ["motif_perte"],
      });
    }
  });
export type SoutirageLiquideInput = z.infer<typeof soutirageLiquideInputSchema>;

export const perteLiquideInputSchema = contexteCommandeLiquideSchema.extend({
  source: prelevementPositionLiquideSchema,
  motif: z.string().trim().min(3).max(500),
  // [] signifie explicitement « aucune affectation perdue ». L'absence ne
  // permet pas de répartir arbitrairement une perte sur une réserve partielle.
  reserve_perdue: z
    .array(z.object({ reserve_id: idSchema, volume_l: volumeLiquidePositifSchema }))
    .max(50)
    .optional(),
});
export type PerteLiquideInput = z.infer<typeof perteLiquideInputSchema>;

export const qualificationHomogeneiteInputSchema = contexteCommandeLiquideSchema.extend({
  position_id: idSchema,
  version_attendue: versionStockLiquideSchema,
  homogeneite: homogeneiteLiquideSchema,
  motif: z.string().trim().min(3).max(500),
});
export type QualificationHomogeneiteInput = z.infer<typeof qualificationHomogeneiteInputSchema>;

export const operationLiquideResultatSchema = z.object({
  operation_id: idSchema,
  sequence: versionStockLiquideSchema,
  deja_enregistree: z.boolean(),
  positions_modifiees: z.array(idSchema),
  lots_crees: z.array(idSchema),
});
export type OperationLiquideResultat = z.infer<typeof operationLiquideResultatSchema>;

const sourceRepriseLiquideSchema = z.object({
  type: z.enum(["vin_clair", "reserve", "assemblage"]),
  id: idSchema,
  libelle: z.string().min(1),
  volume_l: volumeLiquidePositifSchema,
  cuve_id: idSchema.nullable(),
  couleur: z.enum(["blanc", "rouge", "rose", "inconnu"]),
  qualification_couleur: z.enum(["qualifie", "a_qualifier"]),
  vin_clair_id: idSchema.nullable(),
  assemblage_id: idSchema.nullable(),
  reserve_id: idSchema.nullable(),
});

const groupeRepriseLiquideSchema = z.object({
  cle: z.string().min(1),
  libelle: z.string().min(1),
  volume_l: volumeLiquidePositifSchema,
  cuve_id: idSchema.nullable(),
  contenant_existant_id: idSchema.nullable(),
  capacite_l: volumeLiquidePositifSchema.nullable(),
  sources: z.array(sourceRepriseLiquideSchema).min(1),
  couleur: z.enum(["blanc", "rouge", "rose", "inconnu"]),
  qualification_couleur: z.enum(["qualifie", "a_qualifier"]),
  qualification_composition: z.enum(["quantifiee", "partiellement_connue"]),
  vin_clair_id: idSchema.nullable(),
  assemblage_id: idSchema.nullable(),
});

export const previsualisationRepriseLiquideSchema = z.object({
  empreinte_etat: z.string().regex(/^[a-f0-9]{64}$/),
  mode_activation: z.enum(["reprise_initiale", "stock_nul_constate"]),
  date_derniere_operation_legacy: dateIsoSchema.nullable(),
  date_reprise_minimale: dateIsoSchema.nullable(),
  volume_total_l: volumeLiquideSchema,
  nb_lots: z.number().int().nonnegative(),
  nb_positions: z.number().int().nonnegative(),
  groupes: z.array(groupeRepriseLiquideSchema),
  anomalies: z.array(
    z.object({
      code: z.enum([
        "AUCUN_STOCK",
        "SOLDE_NEGATIF",
        "CAPACITE_DEPASSEE",
        "CUVE_INTROUVABLE",
        "TIRAGE_NON_QUALIFIABLE",
        "JOURNAL_CANONIQUE_NON_VIDE",
      ]),
      message: z.string().min(1),
      bloquante: z.literal(true),
      reference: z.string().optional(),
    }),
  ),
});
export type PrevisualisationRepriseLiquide = z.infer<typeof previsualisationRepriseLiquideSchema>;

export const activerRepriseLiquideInputSchema = z.object({
  uuid_commande: z.string().uuid(),
  empreinte_etat: z.string().regex(/^[a-f0-9]{64}$/),
  date_reprise: dateIsoSchema,
  notes: z.string().trim().max(2000).optional(),
});
export type ActiverRepriseLiquideInput = z.infer<typeof activerRepriseLiquideInputSchema>;

export const activerRepriseLiquideResultatSchema = z.object({
  mode_activation: z.enum(["reprise_initiale", "stock_nul_constate"]),
  operation_id: idSchema.nullable(),
  version_bascule: z.string().min(1).max(80),
  empreinte_etat: z.string().regex(/^[a-f0-9]{64}$/),
  date_reprise: dateIsoSchema,
  lots_crees: z.array(idSchema),
  positions_creees: z.array(idSchema),
  deja_enregistree: z.boolean(),
});
export type ActiverRepriseLiquideResultat = z.infer<typeof activerRepriseLiquideResultatSchema>;

export const positionLiquideSortieSchema = z.object({
  id: idSchema,
  lot_id: idSchema,
  contenant_id: idSchema,
  volume_l: volumeLiquideSchema,
  version: versionStockLiquideSchema,
  version_contenant: versionStockLiquideSchema,
  homogeneite: homogeneiteLiquideSchema,
  lot_libelle: z.string().nullable(),
  contenant_libelle: z.string(),
  type_contenant: z.enum(["cuve", "externe", "a_localiser"]),
  cuve_id: idSchema.nullable(),
  nature: natureLotLiquideSchema,
  type_sous_produit: typeSousProduitLiquideSchema.nullable(),
  volume_affecte_reserve_l: volumeLiquideSchema,
});
export type PositionLiquideSortie = z.infer<typeof positionLiquideSortieSchema>;

export const liquidesListerInputSchema = z
  .object({
    contenant_id: idSchema.optional(),
    lot_id: idSchema.optional(),
    vin_clair_id: idSchema.optional(),
    reserve_id: idSchema.optional(),
    recherche: z.string().trim().max(120).optional(),
    inclure_epuises: z.boolean().default(false),
    limite: z.number().int().min(1).max(200).default(50),
    curseur: idSchema.optional(),
  })
  .strict();
export type LiquidesListerInput = z.infer<typeof liquidesListerInputSchema>;

/** Lecture physique minimale : aucune identité de lot, recette ou réserve. */
const moteurNonActifSchema = z.object({
  etat: z.literal("non_active"),
  schema_version: z.null(),
  version_bascule: z.null(),
});
const moteurActifSchema = z.object({
  etat: z.literal("actif"),
  schema_version: z.literal(1),
  version_bascule: z.string().min(1).max(80),
});
const moteurIncompatibleSchema = z.object({
  etat: z.literal("incompatible"),
  schema_version: z
    .number()
    .int()
    .positive()
    .refine((version) => version !== 1),
  version_bascule: z.string().min(1).max(80),
});
export const etatMoteurLiquideSchema = z.discriminatedUnion("etat", [
  moteurNonActifSchema.strict(),
  moteurActifSchema.strict(),
  moteurIncompatibleSchema.strict(),
]);
export type EtatMoteurLiquide = z.infer<typeof etatMoteurLiquideSchema>;

export const liquidesListerResultatSchema = z.discriminatedUnion("etat", [
  moteurNonActifSchema.extend({ positions: z.null(), curseur_suivant: z.null() }).strict(),
  moteurIncompatibleSchema.extend({ positions: z.null(), curseur_suivant: z.null() }).strict(),
  moteurActifSchema
    .extend({
      positions: z.array(positionLiquideSortieSchema),
      curseur_suivant: idSchema.nullable(),
    })
    .strict(),
]);
export type LiquidesListerResultat = z.infer<typeof liquidesListerResultatSchema>;

export const journalLiquideListerInputSchema = z
  .object({
    contenant_id: idSchema.optional(),
    lot_id: idSchema.optional(),
    vin_clair_id: idSchema.optional(),
    limite: z.number().int().min(1).max(100).default(30),
    curseur: z
      .string()
      .regex(/^[1-9]\d{0,18}$/)
      .refine((valeur) => valeur.length < 19 || valeur <= "9223372036854775807")
      .optional(),
  })
  .strict()
  .superRefine((valeur, ctx) => {
    const filtres = [valeur.contenant_id, valeur.lot_id, valeur.vin_clair_id].filter(Boolean);
    if (filtres.length !== 1) {
      ctx.addIssue({
        code: "custom",
        message: "Choisissez exactement une cible pour lire le journal liquide.",
      });
    }
  });
export type JournalLiquideListerInput = z.infer<typeof journalLiquideListerInputSchema>;

const volumeJournalLiquideSchema = z
  .string()
  .regex(/^-?(?:0|[1-9]\d{0,12})(?:\.\d{1,3})?$/)
  .refine((volume) => !/^-0(?:\.0+)?$/.test(volume));

export const ligneJournalLiquideSchema = z
  .object({
    id: idSchema,
    groupe_flux: z.number().int().nonnegative(),
    rang: z.number().int().nonnegative(),
    role: z.string().min(1).max(24),
    volume_l: volumeJournalLiquideSchema,
    volume_traite_l: volumeLiquideSchema.nullable(),
    motif: z.string().nullable(),
    lot_id: idSchema.nullable(),
    lot_libelle: z.string().nullable(),
    position_id: idSchema.nullable(),
    contenant_id: idSchema.nullable(),
    contenant_libelle: z.string().nullable(),
  })
  .strict();
export type LigneJournalLiquide = z.infer<typeof ligneJournalLiquideSchema>;

export const operationJournalLiquideSchema = z
  .object({
    id: idSchema,
    type_operation: z.string().min(1).max(48),
    date_operation: dateIsoSchema,
    heure_operation: z.string().nullable(),
    sequence: z.string().regex(/^[1-9]\d{0,18}$/),
    motif: z.string().nullable(),
    methode_mesure: z.string().nullable(),
    operation_corrigee_id: idSchema.nullable(),
    lignes: z.array(ligneJournalLiquideSchema),
  })
  .strict();
export type OperationJournalLiquide = z.infer<typeof operationJournalLiquideSchema>;

export const journalLiquideListerResultatSchema = z.discriminatedUnion("etat", [
  moteurNonActifSchema.extend({ operations: z.null(), curseur_suivant: z.null() }).strict(),
  moteurIncompatibleSchema.extend({ operations: z.null(), curseur_suivant: z.null() }).strict(),
  moteurActifSchema
    .extend({
      operations: z.array(operationJournalLiquideSchema),
      curseur_suivant: z
        .string()
        .regex(/^[1-9]\d{0,18}$/)
        .nullable(),
    })
    .strict(),
]);
export type JournalLiquideListerResultat = z.infer<typeof journalLiquideListerResultatSchema>;

/** BIGINT PostgreSQL, sans limitation à la précision des nombres JavaScript. */
export const versionLectureStockLiquideSchema = z
  .string()
  .regex(/^(?:0|[1-9]\d{0,18})$/)
  .refine((version) => version.length < 19 || version <= "9223372036854775807");
const contenantPhysiqueSchema = z.object({
  cuve_id: idSchema.nullable(),
  type: z.enum(["cuve", "externe", "a_localiser"]),
  code_cuve: z.string().nullable(),
  libelle: z.string(),
  etat_cuve: z.enum(["vide", "en_service", "nettoyage", "hors_service"]).nullable(),
  capacite_l: volumeLiquidePositifSchema.nullable(),
});
export const occupationContenantLiquideSchema = z.discriminatedUnion("etat_stock", [
  contenantPhysiqueSchema
    .extend({
      etat_stock: z.literal("connu"),
      contenant_id: idSchema,
      volume_l: volumeLiquideSchema,
      place_libre_l: volumeLiquideSchema.nullable(),
      version: versionLectureStockLiquideSchema,
    })
    .strict(),
  contenantPhysiqueSchema
    .extend({
      etat_stock: z.literal("non_reference"),
      type: z.literal("cuve"),
      cuve_id: idSchema,
      contenant_id: z.null(),
      volume_l: z.null(),
      place_libre_l: z.null(),
      version: z.null(),
    })
    .strict(),
]);
export type OccupationContenantLiquide = z.infer<typeof occupationContenantLiquideSchema>;

const curseurContenantLiquideSchema = z.string().regex(/^(?:cuve|contenant):[a-zA-Z0-9_-]{1,32}$/);
export const contenantsLiquidesListerInputSchema = z
  .object({
    cuve_id: idSchema.optional(),
    contenant_id: idSchema.optional(),
    recherche: z.string().trim().max(120).optional(),
    limite: z.number().int().min(1).max(200).default(50),
    curseur: curseurContenantLiquideSchema.optional(),
  })
  .strict();
export type ContenantsLiquidesListerInput = z.infer<typeof contenantsLiquidesListerInputSchema>;

export const contenantsLiquidesResultatSchema = z.discriminatedUnion("etat", [
  moteurNonActifSchema.extend({ contenants: z.null(), curseur_suivant: z.null() }).strict(),
  moteurIncompatibleSchema.extend({ contenants: z.null(), curseur_suivant: z.null() }).strict(),
  moteurActifSchema
    .extend({
      contenants: z.array(occupationContenantLiquideSchema),
      curseur_suivant: curseurContenantLiquideSchema.nullable(),
    })
    .strict(),
]);
export type ContenantsLiquidesResultat = z.infer<typeof contenantsLiquidesResultatSchema>;
